<script lang="ts">
	import {
		getSections,
		NewsSearchSectionTitles,
		type NewsArticleResult,
	} from './utils/getSections';
	import { getVariables } from './utils/getVariables';
	import { isHeniSearch } from './utils/isHeniSearch';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { ArtistCardTag } from '#lib/features/news/components/NewsCard/ArtistCardTag/index.js';
	import { GeoCardTag } from '#lib/features/news/components/NewsCard/GeoCardTag/index.js';
	import { HeniCardTag } from '#lib/features/news/components/NewsCard/HeniCardTag/index.js';
	import { NewCategoryCardTag } from '#lib/features/news/components/NewsCardGrid/NewCategoryCardTag/index.js';
	import { getHeniTagLink } from '#lib/features/news/utils/getHeniTagLink/getHeniTagLink.js';
	import { getSortedCategories } from '#lib/features/news/utils/getSortedCategories/getSortedCategories.js';
	import { gqlClientMutations } from '#lib/gqlClientMutations.js';
	import { getNewsSearch } from '#lib/websites/news/api/getNewsSearch.js';
	import { ArticleSearchCategory } from '#lib/websites/news/constants/categories.js';
	import {
		SearchNewsDocument,
		type SearchNewsQuery,
	} from '#lib/websites/news/mutations/__generated__/searchNews.generated.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { SearchIcon } from '$global/assets/icons/SearchIcon';
	import { Link } from '$global/components/Link';
	import { Txt } from '$global/components/Txt';
	import { WhiteBorderImageContainer } from '$global/components/WhiteBorderImageContainer';
	import { QuerySearch } from '$global/features/search/components/QuerySearch';
	import type { Section } from '$global/features/search/components/QuerySearch/Search';
	import type { SearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { track } from '$global/utils/track';

	interface Props {
		combobox: SearchCombobox | undefined;
		onCloseSearch?: (() => void) | undefined;
		class?: string;
		forceShowGlobalNav?: boolean | undefined;
	}

	let {
		combobox,
		onCloseSearch = undefined,
		class: className = undefined,
		forceShowGlobalNav = undefined,
	}: Props = $props();

	// The URL param is the fallback authority: on error pages (and any route
	// whose load doesn't provide showGlobalNav) page.data is empty, and
	// app-mode (global_nav=0) visitors must stay on navless links.
	let showGlobalNav = $derived(
		forceShowGlobalNav ??
			page.data.showGlobalNav ??
			page.url.searchParams.get('global_nav') !== '0'
	);

	const dataCy = 'news-search';

	// REST ids are integers where GraphQL's `ID` fields are strings; getSections
	// reads names, urls and images only, so the shapes are otherwise the same.
	const requestRest = FeatureFlags.RestPages[
		'NewsSearchSuggestions (news header search)'
	]
		? async (value: string) => {
				const variables = getVariables(value);
				const data = variables
					? await getNewsSearch(fetch, {
							searchPhrase: variables.searchPhrase,
							limit: variables.limit,
						})
					: { searchNews: [] };
				return data as unknown as SearchNewsQuery;
			}
		: undefined;

	const getSeeAllHref = (searchValue: string) => {
		const globalNavParam = showGlobalNav ? '' : 'global_nav=0&';
		return `${GlobalRoutes.NewsHome}?${globalNavParam}phrase=${encodeURIComponent(searchValue)}`;
	};

	export const getRequestError = (error?: string) => {
		if (error) {
			return 'Something went wrong during the search. Please try again later.';
		}
		return null;
	};
</script>

{#snippet articleRow(result: NewsArticleResult, closeSearch: () => void)}
	{@const sortedCategories = getSortedCategories(result.categories)}
	<Link
		href={result.href}
		target="_blank"
		rel="noopener noreferrer"
		class="flex px-6 py-3 hover:bg-gray-50 lg:p-3 lg:px-2"
		onclick={closeSearch}
	>
		{#if result.image?.url}
			<div class="mr-4 h-14 w-14 shrink-0 overflow-hidden bg-[#efefef]">
				<WhiteBorderImageContainer
					class="h-14 w-14 p-1"
					alt={result.line1}
					dataCy={`${dataCy}-result-image`}
					image={result.image}
				/>
			</div>
		{/if}
		<div class="flex flex-col justify-center gap-2">
			<Txt
				dataCy={`${dataCy}-result-line-1`}
				variant="strong1"
				class="line-clamp-3 leading-[1.125] text-wrap">{result.line1}</Txt
			>
			<!-- Pills are visual only here: pointer-events-none lets clicks fall
			through to the article Link so a pill click opens the article, not the
			tag page. -->
			<div class="pointer-events-none flex flex-wrap gap-2">
				{#if sortedCategories.includes(ArticleSearchCategory.shoutouts)}
					<NewCategoryCardTag
						dataCy={`${dataCy}-tag`}
						category={ArticleSearchCategory.shoutouts}
						{forceShowGlobalNav}
					/>
				{/if}
				{#if result.geotagName}
					<GeoCardTag
						showNewVersion
						dataCy={`${dataCy}-tag`}
						name={result.geotagName}
						{forceShowGlobalNav}
					/>
				{/if}
				{#each result.artistNames as artistName}
					<ArtistCardTag
						showNewVersion
						dataCy={`${dataCy}-tag`}
						name={artistName}
						{forceShowGlobalNav}
					/>
				{/each}
			</div>
		</div>
	</Link>
{/snippet}

{#snippet renderTags({
	section,
	onCloseSearch: closeSearch,
	lastItem,
}: {
	section: Section;
	onCloseSearch: () => void;
	lastItem: boolean;
})}
	<div class="flex w-[100%] flex-col lg:w-[75%]">
		{#if section.title === NewsSearchSectionTitles.Articles}
			{#each section.results as result}
				{@render articleRow(result as NewsArticleResult, closeSearch)}
			{/each}
		{:else}
			<div class="flex flex-wrap gap-2 px-5 py-3 lg:px-2">
				{#each section.results as result}
					{#if section.title === NewsSearchSectionTitles.Heni}
						<HeniCardTag
							showNewVersion
							dataCy={`${dataCy}-tag`}
							onClick={closeSearch}
							{forceShowGlobalNav}
						/>
					{:else if section.title === NewsSearchSectionTitles.Geotags}
						<GeoCardTag
							showNewVersion
							dataCy={`${dataCy}-tag`}
							name={result.line1}
							onClick={closeSearch}
							{forceShowGlobalNav}
						/>
					{:else}
						<ArtistCardTag
							showNewVersion
							dataCy={`${dataCy}-tag`}
							name={result.line1}
							onClick={closeSearch}
							{forceShowGlobalNav}
						/>
					{/if}
				{/each}
			</div>
		{/if}
		{#if !lastItem}
			<div class="w-full border-b border-gray-200"></div>
		{/if}
	</div>
{/snippet}

{#snippet renderHeader({
	searchValue,
	onCloseSearch: closeSearch,
}: {
	searchValue: string;
	onCloseSearch: () => void;
})}
	<Link
		href={getSeeAllHref(searchValue)}
		class="flex items-center gap-2 border-b border-gray-200 py-2.5 pr-6 pl-5 hover:bg-gray-50"
		onclick={closeSearch}
	>
		<SearchIcon class="h-6 w-6 shrink-0" />
		<Txt
			dataCy={`${dataCy}-see-all`}
			variant="strong1"
			class="min-w-0 translate-y-[-1px] truncate uppercase underline"
		>
			See all results for <span class="normal-case">"{searchValue}"</span>
		</Txt>
	</Link>
{/snippet}

<QuerySearch
	emptyValueResponse={{ searchNews: [] }}
	{combobox}
	{dataCy}
	{onCloseSearch}
	class={className}
	document={SearchNewsDocument}
	graphQlClient={gqlClientMutations}
	getError={getRequestError}
	noResultsMessage="No suggestions found"
	classes={{
		tagTitle: 'mt-2.5',
		resultsList: 'pt-0',
		section: (i) =>
			`[&>p:empty]:hidden lg:[&>p:empty]:block ${i ? 'pb-1' : 'pb-0'}`,
	}}
	getSections={(data, searchValue) =>
		getSections(data, showGlobalNav, searchValue)}
	{getVariables}
	request={requestRest}
	{goto}
	{track}
	onEnter={(searchValue) =>
		goto(
			isHeniSearch(searchValue)
				? getHeniTagLink(showGlobalNav)
				: getSeeAllHref(searchValue)
		)}
	{renderTags}
	{renderHeader}
	mixpanelPrefix="NEWS"
/>
